import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Sparkles, Calendar, Heart, Target } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function YearReview({ eventId, isOwner }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    author_name: "",
    year: new Date().getFullYear(),
    highlights: "",
    learnings: "",
    grateful_for: "",
    goals_next_year: ""
  });

  useEffect(() => {
    fetchReviews();
  }, [eventId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/year-review`);
      setReviews(response.data);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.author_name || !formData.highlights) {
      toast.error("Name und Highlights sind erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/year-review`,
        formData
      );
      
      toast.success("Jahresrückblick geteilt!");
      setFormData({ 
        author_name: "", 
        year: new Date().getFullYear(),
        highlights: "",
        learnings: "",
        grateful_for: "",
        goals_next_year: ""
      });
      fetchReviews();
    } catch (error) {
      console.error("Failed to add review:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-primary" />
          Jahresrückblick
        </h3>
        <p className="text-sm text-muted-foreground">
          Teile deine Highlights, Learnings und Ziele für das neue Jahr
        </p>
      </div>

      {/* Add Review Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Dein Name *</Label>
            <Input
              value={formData.author_name}
              onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
              placeholder="Name"
              required
            />
          </div>
          <div>
            <Label>Jahr</Label>
            <Input
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
            />
          </div>
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Highlights des Jahres *
          </Label>
          <Textarea
            value={formData.highlights}
            onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
            placeholder="Was waren deine schönsten Momente?"
            rows={3}
            required
          />
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Learnings & Erkenntnisse
          </Label>
          <Textarea
            value={formData.learnings}
            onChange={(e) => setFormData({ ...formData, learnings: e.target.value })}
            placeholder="Was hast du gelernt?"
            rows={2}
          />
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <Heart className="w-4 h-4" />
            Dankbar für...
          </Label>
          <Textarea
            value={formData.grateful_for}
            onChange={(e) => setFormData({ ...formData, grateful_for: e.target.value })}
            placeholder="Wofür bist du dankbar?"
            rows={2}
          />
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Ziele fürs neue Jahr
          </Label>
          <Textarea
            value={formData.goals_next_year}
            onChange={(e) => setFormData({ ...formData, goals_next_year: e.target.value })}
            placeholder="Was willst du erreichen?"
            rows={2}
          />
        </div>

        <Button type="submit" className="w-full">
          Rückblick teilen
        </Button>
      </form>

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Sparkles className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Noch keine Rückblicke</p>
            <p className="text-xs mt-1">Teile deinen als Erster!</p>
          </div>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="p-4 bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg border">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-medium">{review.author_name}</p>
                  <p className="text-xs text-muted-foreground">Rückblick {review.year}</p>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                {review.highlights && (
                  <div>
                    <p className="font-medium text-xs text-muted-foreground flex items-center gap-1 mb-1">
                      <Calendar className="w-3 h-3" /> Highlights
                    </p>
                    <p className="text-foreground">{review.highlights}</p>
                  </div>
                )}

                {review.learnings && (
                  <div>
                    <p className="font-medium text-xs text-muted-foreground flex items-center gap-1 mb-1">
                      <Sparkles className="w-3 h-3" /> Learnings
                    </p>
                    <p className="text-foreground">{review.learnings}</p>
                  </div>
                )}

                {review.grateful_for && (
                  <div>
                    <p className="font-medium text-xs text-muted-foreground flex items-center gap-1 mb-1">
                      <Heart className="w-3 h-3" /> Dankbar für
                    </p>
                    <p className="text-foreground">{review.grateful_for}</p>
                  </div>
                )}

                {review.goals_next_year && (
                  <div>
                    <p className="font-medium text-xs text-muted-foreground flex items-center gap-1 mb-1">
                      <Target className="w-3 h-3" /> Ziele
                    </p>
                    <p className="text-foreground">{review.goals_next_year}</p>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}